import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AnalyzeParametersDict } from '../../interfaces/analyze-parameters-dict';

@Injectable({ providedIn: 'root' })
export class AnalyzeParametersService {

  constructor(private translate: TranslateService) { }

  private get currentLocale() {
    const lang = this.translate.currentLang || this.translate.defaultLang || 'pt';
    const normalized = lang.toLowerCase();
    if (normalized.startsWith('pt')) {
      return 'pt-BR';
    }
    if (normalized.startsWith('es')) {
      return 'es-ES';
    }
    if (normalized.startsWith('en')) {
      return 'en-US';
    }
    return lang;
  }

  private formatNumber(value: number, fractionDigits: number) {
    return new Intl.NumberFormat(this.currentLocale, {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(value);
  }

  private numberOption(value: string) {
    const numericValue = Number(value);
    const fractionDigits = value.includes('.') ? value.split('.')[1].length : 0;
    return { value, name: this.formatNumber(numericValue, fractionDigits) };
  }

  private unavailableOption(value = '99') {
    return { name: 'SHARED.ANALYZE_PARAMETERS.OPTIONS.UNAVAILABLE', value };
  }

  public get watersOptions() {
    return [
      { name: 'SHARED.ANALYZE_PARAMETERS.WATERS.INDISPONIVEL', value: 'INDISPONIVEL' },
      { name: 'SHARED.ANALYZE_PARAMETERS.WATERS.CRISTALINA_BRILHOSA', value: 'CRISTALINA_BRILHOSA' },
      { name: 'SHARED.ANALYZE_PARAMETERS.WATERS.TRANSPARENTE_OPACA', value: 'TRANSPARENTE_OPACA' },
      { name: 'SHARED.ANALYZE_PARAMETERS.WATERS.LEVEMENTE_TURVA', value: 'LEVEMENTE_TURVA' },
      { name: 'SHARED.ANALYZE_PARAMETERS.WATERS.TURVA', value: 'TURVA' },
      { name: 'SHARED.ANALYZE_PARAMETERS.WATERS.ESVERDEADA', value: 'ESVERDEADA' },
      { name: 'SHARED.ANALYZE_PARAMETERS.WATERS.VERDE', value: 'VERDE' },
      { name: 'SHARED.ANALYZE_PARAMETERS.WATERS.OUTRA_COLORACAO', value: 'OUTRA_COLORACAO' },
    ];
  }

  public get defaultPhysicalOptions() {
    return [
      { name: 'SHARED.ANALYZE_PARAMETERS.OPTIONS.UNAVAILABLE', value: 'INDISPONIVEL' },
      { name: 'SHARED.ANALYZE_PARAMETERS.PHYSICAL_CLEANNESS.MUITO_LIMPO', value: 'MUITO_LIMPO' },
      { name: 'SHARED.ANALYZE_PARAMETERS.PHYSICAL_CLEANNESS.LIMPO', value: 'LIMPO' },
      { name: 'SHARED.ANALYZE_PARAMETERS.PHYSICAL_CLEANNESS.SUJO', value: 'SUJO' },
      { name: 'SHARED.ANALYZE_PARAMETERS.PHYSICAL_CLEANNESS.MUITO_SUJO', value: 'MUITO_SUJO' },
    ];
  }

  public get physicalParametersDict(): AnalyzeParametersDict {
    return {
      Água: {
        aiAnalyzeKey: 'aiWater',
        analyzeKey: 'water',
        options: this.watersOptions,
      },
      Temperatura: {
        analyzeKey: 'temperature',
        options: [this.unavailableOption()]
          .concat([...Array(29).keys()].map((value): any => {
            const temperature = (value + 8);
            const temperatureValue = temperature.toString();
            return { value: temperatureValue, name: this.formatNumber(temperature, 0) };
          })),
      },
      Fundo: {
        analyzeKey: 'bottom',
        options: this.defaultPhysicalOptions,
      },
      Superfície: {
        analyzeKey: 'surface',
        options: this.defaultPhysicalOptions,
      },
      Borda: {
        analyzeKey: 'edge',
        options: this.defaultPhysicalOptions,
      },
    };
  }

  public get chemicalParametersDict(): AnalyzeParametersDict {
    return {
      'Cloro Total': {
        analyzeKey: 'totalChlorine',
        options: [
          this.unavailableOption(),
          this.numberOption('0'),
          this.numberOption('0.5'),
          this.numberOption('0.8'),
          this.numberOption('1'),
          this.numberOption('1.5'),
          this.numberOption('2'),
          this.numberOption('3'),
          this.numberOption('4'),
          this.numberOption('5'),
          this.numberOption('10'),
        ],
      },
      'Cloro Livre': {
        analyzeKey: 'freeChlorine',
        aiAnalyzeKey: 'aiFreeChlorine',
        options: [
          this.unavailableOption(),
          this.numberOption('0'),
          this.numberOption('0.5'),
          this.numberOption('1'),
          this.numberOption('2'),
          this.numberOption('3'),
          this.numberOption('5'),
          this.numberOption('10'),
        ],
      },
      pH: {
        analyzeKey: 'ph',
        aiAnalyzeKey: 'aiPh',
        options: [
          this.unavailableOption(),
          this.numberOption('6.2'),
          this.numberOption('6.4'),
          this.numberOption('6.8'),
          this.numberOption('7.2'),
          this.numberOption('7.4'),
          this.numberOption('7.6'),
          this.numberOption('7.8'),
          this.numberOption('8.0'),
          this.numberOption('8.4'),
        ],
      },
      Alcalinidade: {
        analyzeKey: 'alkalinity',
        aiAnalyzeKey: 'aiAlkalinity',
        options: [
          this.unavailableOption(),
          this.numberOption('0'),
          this.numberOption('20'),
          this.numberOption('40'),
          this.numberOption('60'),
          this.numberOption('80'),
          this.numberOption('100'),
          this.numberOption('120'),
          this.numberOption('180'),
          this.numberOption('240'),
        ],
      },
      Cianúrico: {
        analyzeKey: 'cyanuric',
        aiAnalyzeKey: 'aiCyanuric',
        options: [
          this.unavailableOption(),
          this.numberOption('0'),
          { name: '30-50', value: '40' },
          this.numberOption('100'),
          this.numberOption('150'),
        ],
      },
      Dureza: {
        analyzeKey: 'hardness',
        aiAnalyzeKey: 'aiHardness',
        options: [
          this.unavailableOption(),
          this.numberOption('0'),
          this.numberOption('100'),
          this.numberOption('200'),
          this.numberOption('400'),
          this.numberOption('500'),
          this.numberOption('600'),
          this.numberOption('1000'),
        ],
      },
      Sal: {
        analyzeKey: 'salt',
        options: [this.unavailableOption()]
          .concat([...Array(15).keys()].map((value): any => {
            const optionValue = (value * 5).toString();
            return this.numberOption(optionValue);
          })),
      },
    };
  }

  public get othersChemicalParametersDict(): AnalyzeParametersDict {
    return {
      gH: {
        isOthers: true,
        analyzeKey: 'gh',
        options: [
          { name: '0', value: '0' },
          { name: '4', value: '4' },
          { name: '8', value: '8' },
          { name: '16', value: '16' },
        ],
      },
      kH: {
        isOthers: true,
        analyzeKey: 'kh',
        options: [
          { name: '0', value: '0' },
          { name: '3', value: '3' },
          { name: '6', value: '6' },
          { name: '10', value: '10' },
          { name: '15', value: '15' },
          { name: '20', value: '20' },
        ],
      },
      'Nitrito (NO2)': {
        isOthers: true,
        analyzeKey: 'nitrite',
        options: [
          this.numberOption('0'),
          this.numberOption('0.5'),
          this.numberOption('2.0'),
          this.numberOption('5.0'),
          this.numberOption('10.0'),
        ],
      },
      'Nitrato (NO3)': {
        isOthers: true,
        analyzeKey: 'nitrate',
        options: [
          { name: '0', value: '0' },
          { name: '10', value: '10' },
          { name: '25', value: '25' },
          { name: '50', value: '50' },
          { name: '100', value: '100' },
          { name: '250', value: '250' },
        ],
      },
      'Amônia Total': {
        isOthers: true,
        analyzeKey: 'totalAmmonia',
        options: [
          this.numberOption('0'),
          this.numberOption('0.5'),
          this.numberOption('1.0'),
          this.numberOption('2.0'),
          this.numberOption('5.0'),
          this.numberOption('10.0'),
        ],
      },
    };
  }

  public get othersPhysicalParametersDict() {
    return {
      Calçada: {
        isOthers: true,
        analyzeKey: 'sidewalk',
        options: this.defaultPhysicalOptions,
      },
      Escada: {
        isOthers: true,
        analyzeKey: 'stairs',
        options: this.defaultPhysicalOptions,
      },
      'Limpeza da casa de maquinas': {
        isOthers: true,
        analyzeKey: 'machineHouse',
        options: this.defaultPhysicalOptions,
      },
      'Funcionamento da casa de maquinas': {
        isOthers: true,
        analyzeKey: 'machineHouseIsWorking',
        options: [
          { name: 'SHARED.ANALYZE_PARAMETERS.MACHINE_HOUSE.WORKING', value: 'true' },
          { name: 'SHARED.ANALYZE_PARAMETERS.MACHINE_HOUSE.DEFECTIVE', value: 'false' },
        ],
      },
    };
  }
}
